import { useEffect, useRef, useState } from 'react'
import { CAM_SIZE, CamLayout } from '../lib/types'

interface Props {
  canvas: HTMLCanvasElement
  layout: CamLayout
  live: boolean
  onLayout(patch: Partial<CamLayout>): void
}

/**
 * The live preview. The <canvas> shown here IS the canvas being recorded, so
 * this is genuinely what-you-see-is-what-you-get. On top of it we lay a
 * transparent HTML handle the user can drag; it only ever writes
 * normalised numbers back into the layout.
 */
export function Stage({ canvas, layout, live, onLayout }: Props) {
  const hostRef = useRef<HTMLDivElement>(null)
  const boxRef = useRef<HTMLDivElement>(null)
  const [ready, setReady] = useState(false)

  // Move the real canvas element into this container (it's owned by the engine).
  useEffect(() => {
    const host = hostRef.current
    if (!host) return
    host.appendChild(canvas)
    setReady(true)
    return () => { if (canvas.parentElement === host) host.removeChild(canvas) }
  }, [canvas])

  /** Pixel rect of the canvas as actually painted (object-fit: contain letterboxes it). */
  const paintedRect = () => {
    const host = hostRef.current!
    const hb = host.getBoundingClientRect()
    const ar = canvas.width / canvas.height || 16 / 9
    let w = hb.width
    let h = w / ar
    if (h > hb.height) { h = hb.height; w = h * ar }
    return { left: hb.left + (hb.width - w) / 2, top: hb.top + (hb.height - h) / 2, width: w, height: h }
  }

  const startDrag = (e: React.PointerEvent) => {
    e.preventDefault()
    const target = e.currentTarget as HTMLElement
    target.setPointerCapture(e.pointerId)
    const rect = paintedRect()

    const onMove = (ev: PointerEvent) => {
      const x = (ev.clientX - rect.left) / rect.width
      const y = (ev.clientY - rect.top) / rect.height
      onLayout({ x: clamp(x, 0.02, 0.98), y: clamp(y, 0.02, 0.98) })
    }
    const onUp = () => {
      window.removeEventListener('pointermove', onMove)
      window.removeEventListener('pointerup', onUp)
    }
    window.addEventListener('pointermove', onMove)
    window.addEventListener('pointerup', onUp)
  }

  // Arrow keys nudge the bubble — handy for nailing an exact corner.
  const onKey = (e: React.KeyboardEvent) => {
    const step = e.shiftKey ? 0.05 : 0.01
    const map: Record<string, Partial<CamLayout>> = {
      ArrowLeft: { x: clamp(layout.x - step, 0.02, 0.98) },
      ArrowRight: { x: clamp(layout.x + step, 0.02, 0.98) },
      ArrowUp: { y: clamp(layout.y - step, 0.02, 0.98) },
      ArrowDown: { y: clamp(layout.y + step, 0.02, 0.98) },
    }
    const patch = map[e.key]
    if (patch) { e.preventDefault(); onLayout(patch) }
  }

  // Recompute the handle's on-screen box whenever anything moves.
  useEffect(() => {
    const place = () => {
      const box = boxRef.current
      const host = hostRef.current
      if (!box || !host) return
      const r = paintedRect()
      const hb = host.getBoundingClientRect()
      const w = CAM_SIZE * r.width
      box.style.left = `${r.left - hb.left + layout.x * r.width}px`
      box.style.top = `${r.top - hb.top + layout.y * r.height}px`
      box.style.width = `${w}px`
      box.style.height = `${w}px`
    }
    place()
    const ro = new ResizeObserver(place)
    if (hostRef.current) ro.observe(hostRef.current)
    window.addEventListener('resize', place)
    return () => { ro.disconnect(); window.removeEventListener('resize', place) }
  }, [layout, ready])

  return (
    <div className="stage" ref={hostRef}>
      {!layout.hidden && (
        <div
          ref={boxRef}
          className="bubble"
          tabIndex={0}
          role="application"
          aria-label="Camera position. Drag to move, or use arrow keys."
          onPointerDown={startDrag}
          onKeyDown={onKey}
        >
          <span className="hint">{live ? 'drag me — even while recording' : 'drag me'}</span>
        </div>
      )}
    </div>
  )
}

const clamp = (v: number, lo: number, hi: number) => Math.min(hi, Math.max(lo, v))
